import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Edit2, Save, X, Trash2, Plus, PlusCircle, Paperclip, Download, AlertTriangle, Printer, FileText, Search, ArrowUpDown, Eye, Filter, ExternalLink } from "lucide-react";

// ── Header constants (stable references — prevent child re-renders) ────────────
const HEADERS_DOCS = ["Designação","Ficheiro(s)","Data","Ações"];
const HEADERS_HON  = ["Designação","Débito/Crédito","Data","Tipo Pagamento","Estado","Ações"];
const HEADERS_REU  = ["Data Início","Resumo","Intervenientes","Ações"];
const HEADERS_DES  = ["Designação","€ Valor","Data","Tipo","Saldado","Ações"];
const HEADERS_PRA  = ["Data","Respondido","Descrição","Ações"];
import { chunkedUpload } from "@/utils/chunkedUpload";

// ── Module-level helpers ──────────────────────────────────────────────────────
/** Converte uma lista CSV de nomes de ficheiros FTP em objetos {key, url, displayName} */
function parseFtpLinks(original_filename) {
  if (!original_filename) return [];
  const base = process.env.REACT_APP_BACKEND_URL;
  return original_filename.split(',').filter(Boolean).map(fn => {
    const cleanFn = fn.trim();
    return {
      key: cleanFn,
      url: `${base}/api/ftp-file?fname=${encodeURIComponent(cleanFn)}`,
      displayName: cleanFn.replace(/^\d+_/, ''),
    };
  });
}

const SITUACOES = ["Novo","Em curso","Validar","Findo","Aguarda Documentos/Informações","Aguarda Pagamento"];
const ENTIDADES = ["","Tribunal","SEF","AT","C.R.Civil","C.R.Centrais","C.Municipal","IMT","MNE","Outra"];
const SITUACAO_COLORS = { "Novo":"bg-yellow-100 text-yellow-800","Em curso":"bg-blue-100 text-blue-800","Validar":"bg-purple-100 text-purple-800","Findo":"bg-green-100 text-green-800","Aguarda Documentos/Informações":"bg-orange-100 text-orange-800","Aguarda Pagamento":"bg-indigo-100 text-indigo-800" };
const TIPO_PAGAMENTO = ["MB","Numerário","MBWay","Transferência","Depósito"];

function Tab({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active?"border-[#0b679e] text-[#0b679e]":"border-transparent text-slate-500 hover:text-slate-700"}`}>
      {label}
    </button>
  );
}

function Field({ label, value, edit, type="text", name, form, setForm, options }) {
  const cls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]";
  let control = null;
  if (edit) {
    if (options) {
      control = (
        <select value={form?.[name]||""} onChange={e=>setForm({...form,[name]:e.target.value})} className={cls}>
          {options.map(o=><option key={o} value={o}>{o||"—"}</option>)}
        </select>
      );
    } else if (type === "textarea") {
      control = <textarea value={form?.[name]||""} onChange={e=>setForm({...form,[name]:e.target.value})} rows={3} className={cls}/>;
    } else {
      control = <input type={type} value={form?.[name]||""} onChange={e=>setForm({...form,[name]:e.target.value})} className={cls}/>;
    }
  }
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      {edit ? control : <p className="text-sm text-slate-900 min-h-[22px]">{value || "—"}</p>}
    </div>
  );
}

function NotaHonorariosModal({ processo, honorarios, onClose }) {
  const today = new Date().toLocaleDateString("pt-PT");
  const total = honorarios.reduce((s, h) => {
    const v = parseFloat(String(h.debito_credito || 0).replace(",", ".")) || 0;
    return s + v;
  }, 0);
  const fmtVal = (v) => Number(v || 0).toFixed(2).replace(".", ",") + " €";
  const fmtTotal = total.toFixed(2).replace(".", ",") + " €";

  const handlePrint = () => {
    const base = window.location.origin;
    const logoUrl = `${base}/assets/logo.png`;
    const sigUrl  = `${base}/assets/assinatura_rui.png`;
    const esc = (s) => String(s || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

    const tableRows = honorarios.map(h => `
      <tr>
        <td style="text-align:center;white-space:nowrap">${esc(h.data || "—")}</td>
        <td>${esc(h.designacao || h.descricao || "—")}</td>
        <td style="text-align:right;white-space:nowrap">${fmtVal(h.debito_credito)}</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html>
<html lang="pt"><head><meta charset="UTF-8">
<title>Nota de Honorários</title>
<style>
  @page{margin:2cm 2.5cm;size:A4}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;font-size:10pt;color:#1a1a1a;background:#fff}
  .hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px}
  .logo img{height:62px;width:auto}
  .fi{text-align:right;line-height:1.55}
  .fi .fn{font-size:10pt;font-weight:bold;color:#0B679E}
  .fi .fd{font-size:7.5pt;color:#555}
  .fi .fw{font-size:7.5pt;color:#0B679E}
  hr.sep{border:none;border-top:2.5px solid #0B679E;margin:6px 0 10px}
  .title{font-size:13pt;font-weight:bold;color:#0B679E;margin:8px 0}
  .refdate{display:flex;justify-content:space-between;font-size:8.5pt;color:#555;margin-bottom:10px}
  .cbox{background:#EBF4FB;border-left:3px solid #0B679E;padding:7px 12px;margin-bottom:14px}
  .cbox .ac{font-size:7.5pt;color:#666;margin-bottom:2px}
  .cbox .cn{font-size:11pt;font-weight:bold;color:#0B679E}
  p{margin-bottom:7px;line-height:1.65;font-size:10pt;text-align:justify}
  table.hon{width:100%;border-collapse:collapse;margin:10px 0 14px;font-size:9pt}
  table.hon thead th{background:#0B679E;color:#fff;padding:6px 8px;font-weight:bold;border:1px solid #0B679E}
  table.hon tbody td{padding:5px 8px;border:1px solid #ccc;vertical-align:top}
  table.hon tbody tr:nth-child(even) td{background:#F4F9FD}
  table.hon tfoot td{background:#EBF4FB;font-weight:bold;color:#0B679E;border:1.5px solid #0B679E;padding:6px 8px;font-size:10pt}
  .bbox{display:flex;margin:10px 0 14px}
  .beur{background:#0B679E;color:#fff;padding:0 16px;font-size:18pt;font-weight:bold;display:flex;align-items:center;justify-content:center}
  .binfo{background:#EBF4FB;padding:8px 14px;flex:1;font-size:9pt;line-height:1.9}
  .binfo b{color:#0B679E}
  .sig{margin-top:16px}
  .sig .lbl{font-size:10pt;margin-bottom:3px}
  .sig img{height:65px;width:auto;display:block;margin:4px 0 2px}
  .sig .nm{font-size:10pt}
  .tbox{display:flex;justify-content:space-between;align-items:center;background:#EBF4FB;border:2px solid #0B679E;padding:10px 16px;margin-top:22px}
  .tbox .tl{font-size:12pt;font-weight:bold;color:#0B679E;text-transform:uppercase;letter-spacing:.05em}
  .tbox .tv{font-size:20pt;font-weight:bold;color:#0B679E}
  hr.fsep{border:none;border-top:1px solid #0B679E;margin:18px 0 5px}
  .ftxt{font-size:7pt;color:#888;text-align:center}
  @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
</style></head>
<body>
<div class="hdr">
  <div class="logo"><img src="${logoUrl}" alt="ACD" onerror="this.style.display='none'"></div>
  <div class="fi">
    <div class="fn">Ana Cristina Domingues Advogados</div>
    <div class="fd">Av. Dom João II Nº 3 B, Loja 22  |  1990-607 Lisboa</div>
    <div class="fd">Tel: 21 716 76 64  |  21 711 29 28</div>
    <div class="fd">Açores: Av. Natália Correia 2, 9500-341 Ponta Delgada</div>
    <div class="fw">www.acd-advogados.net</div>
  </div>
</div>
<hr class="sep">
<div class="title">NOTA DE HONORÁRIOS E/OU DESPESAS</div>
<div class="refdate">
  <span>Ref.: ${esc(processo.numero_processo || "—")}</span>
  <span>Lisboa, ${today}</span>
</div>
<div class="cbox">
  <div class="ac">A/C de:</div>
  <div class="cn">${esc((processo.nome_cliente || "").toUpperCase())}</div>
</div>
<p>Exm.ºs Senhores,</p>
<p>Serve o presente para remeter a V. Exa. Nota de Honorários e/ou Despesas referente aos trabalhos prestados até à presente data.</p>
<p>Como poderá verificar pela análise da tabela que infra se remete, encontra-se por liquidar a importância total aí referida, cujo pagamento se solicita:</p>
<table class="hon">
  <thead><tr>
    <th style="text-align:center;width:18%">Data</th>
    <th>Designação</th>
    <th style="text-align:right;width:22%">Débito/Crédito</th>
  </tr></thead>
  <tbody>${tableRows}</tbody>
  <tfoot><tr>
    <td colspan="2">TOTAL DE HONORÁRIOS</td>
    <td style="text-align:right">${fmtTotal}</td>
  </tr></tfoot>
</table>
<p>Poderá proceder ao pagamento através de depósito ou transferência bancária para a conta que passo a indicar:</p>
<div class="bbox">
  <div class="beur">€</div>
  <div class="binfo">
    <div><b>Banco:</b> NOVO BANCO</div>
    <div><b>IBAN:</b> PT50 0007 0000 0051 7221 7522 3</div>
    <div><b>SWIFT / BIC:</b> BESCPTPL</div>
  </div>
</div>
<p>Após o pagamento agradeço envio do respetivo comprovativo, para a morada do N/ escritório ou via e-mail para o endereço indicado em nota de rodapé, a fim de o alocarmos ao V/ dossier.</p>
<p style="margin-bottom:20px">Sem outro assunto de momento, subscrevemo-nos com elevada consideração.</p>
<div class="sig">
  <div class="lbl">Pela Advogada,</div>
  <img src="${sigUrl}" alt="Assinatura" onerror="this.style.display='none'">
  <div class="nm">(Rui Pires – Responsável Financeiro)</div>
</div>
<div class="tbox">
  <span class="tl">Total a Liquidar</span>
  <span class="tv">${fmtTotal}</span>
</div>
<hr class="fsep">
<div class="ftxt">Ana Cristina Domingues Advogados  |  Av. Dom João II Nº 3 B, Loja 22 · 1990-607 Lisboa  |  Tel: 21 716 76 64  |  www.acd-advogados.net</div>
</body></html>`;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=920,height=780");
    if (w) {
      w.addEventListener("load", () => {
        setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 400);
      });
    }
  };

  const handleDownloadWord = async () => {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/processos/${processo.id}/nota-honorarios`,
        { credentials: "include" }
      );
      if (!resp.ok) {
        alert("Erro ao descarregar o documento. Verifique se está autenticado e tente novamente.");
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const num = processo.numero_processo ? processo.numero_processo.replace(/\//g, "_") : processo.id;
      a.download = `Nota_Honorarios_${num}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erro de rede ao descarregar o documento. Verifique a ligação e tente novamente.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Nota de Honorários</span>
            <span className="bg-[#0b679e] text-white text-xs font-bold px-2.5 py-1 rounded-full">Total: {fmtTotal}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="btn-imprimir-nota"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-white transition-colors text-slate-700">
              <Printer size={13} /> Imprimir
            </button>
            <button onClick={handleDownloadWord} data-testid="btn-download-word"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white transition-colors"
              style={{ backgroundColor: "#0b679e" }}>
              <FileText size={13} /> Descarregar Word
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 transition-colors">
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Document preview */}
        <div id="nota-hon-content" className="p-10 text-sm leading-relaxed font-sans text-slate-900 max-h-[65vh] overflow-y-auto" style={{ fontFamily: "Arial, sans-serif" }}>
          <p className="mb-1">Exm.ºs Senhores,</p>
          <p className="font-bold mb-1">{(processo.nome_cliente || "").toUpperCase()}</p>
          <p className="text-right mb-4">{today}</p>
          <p className="font-bold mb-6">Assunto: Nota de Honorários e/ou Despesas</p>

          <p className="mb-3">Exm.ºs Senhores,</p>
          <p className="mb-3 text-justify">Serve o presente para remeter a V. Exa. Nota de Honorários e/ou Despesas referente aos trabalhos prestados até à presente data.</p>
          <p className="mb-5 text-justify">Como poderá verificar pela análise da tabela que infra se remete, encontra-se por liquidar a importância total aí referida, cujo pagamento se solicita:</p>

          <p className="font-bold mb-2">Nota de Honorários</p>
          <table className="w-full border-collapse mb-5" style={{ border: "1px solid #999", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <th className="border border-slate-400 px-3 py-1.5 text-center text-xs font-bold w-24">Data</th>
                <th className="border border-slate-400 px-3 py-1.5 text-center text-xs font-bold">Designação</th>
                <th className="border border-slate-400 px-3 py-1.5 text-center text-xs font-bold w-28">Débito/Crédito</th>
              </tr>
            </thead>
            <tbody>
              {honorarios.map((h) => (
                <tr key={h.id || h._id || h.designacao}>
                  <td className="border border-slate-300 px-3 py-1.5 text-xs">{h.data || "—"}</td>
                  <td className="border border-slate-300 px-3 py-1.5 text-xs">{h.designacao || h.descricao || "—"}</td>
                  <td className="border border-slate-300 px-3 py-1.5 text-xs text-right">{fmtVal(h.debito_credito)}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: "#f9f9f9" }}>
                <td colSpan={2} className="border border-slate-400 px-3 py-1.5 text-xs font-bold">Total de honorários</td>
                <td className="border border-slate-400 px-3 py-1.5 text-xs font-bold text-right">{fmtTotal}</td>
              </tr>
            </tbody>
          </table>

          <p className="mb-3 text-justify">Poderá proceder ao pagamento através de depósito ou transferência bancária para a conta que passo a indicar:</p>
          <div className="mb-5 space-y-1">
            <p><strong>Banco:</strong> NOVO BANCO</p>
            <p><strong>IBAN:</strong> PT50 0007 0000 0051 7221 7522 3</p>
            <p><strong>SWIFT / BIC:</strong> BESCPTPL</p>
          </div>

          <p className="mb-3 text-justify">Após o pagamento agradeço envio do respetivo comprovativo, para a morada do N/ escritório ou via e-mail para o endereço indicado em nota de rodapé, a fim de o alocarmos ao V/ dossier.</p>
          <p className="mb-8">Sem outro assunto de momento, subscrevemo-nos com consideração.</p>
          <p className="mb-1">Pela Advogada,</p>
          <img src="/assets/assinatura_rui.png" alt="Assinatura" style={{ height: "60px", width: "auto", display: "block", margin: "6px 0 4px" }} />
          <p>(Rui Pires – Responsável Financeiro)</p>

          <div className="mt-6 p-4 rounded-lg flex items-center justify-between" style={{ backgroundColor: "#d6e9f5", border: "2px solid #0b679e", breakInside: "avoid" }}>
            <span style={{ fontSize: "13px", fontWeight: "bold", color: "#0b679e", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total a Liquidar</span>
            <span style={{ fontSize: "22px", fontWeight: "bold", color: "#0b679e" }}>{fmtTotal}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubEntityTable({ headers, rows, onAdd, onDelete, renderRow, loading, totalFooter }) {
  const renderBody = () => {
    if (loading) return <div className="text-center py-6 text-slate-400 text-sm">A carregar...</div>;
    if (rows.length === 0) return <div className="text-center py-8 text-slate-400 text-sm">Sem registos</div>;
    return (
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>{headers.map(h => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => renderRow(r, i, onDelete))}
          </tbody>
          {totalFooter && <tfoot>{totalFooter}</tfoot>}
        </table>
      </div>
    );
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={onAdd} data-testid="add-subentity-btn"
          className="flex items-center gap-2 bg-[#0b679e] hover:bg-[#084e78] text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
          <Plus size={14}/> Adicionar
        </button>
      </div>
      {renderBody()}
    </div>
  );
}

export default function ProcessoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [processo, setProcesso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("cliente");
  const [users, setUsers] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subForm, setSubForm] = useState({});
  // Sub-entities
  const [documentos, setDocumentos] = useState([]);
  const [honorarios, setHonorarios] = useState([]);
  const [reunioes, setReunioes] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [prazos, setPrazos] = useState([]);
  // Modals
  const [showDocModal, setShowDocModal] = useState(false);
  const [showHonModal, setShowHonModal] = useState(false);
  const [showReuModal, setShowReuModal] = useState(false);
  const [showDesModal, setShowDesModal] = useState(false);
  const [editDes, setEditDes] = useState(null);
  const [showPraModal, setShowPraModal] = useState(false);
  const [showNota, setShowNota] = useState(false);
  const [editHon, setEditHon] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docFiles, setDocFiles] = useState([]);
  const [docUploadError, setDocUploadError] = useState("");
  const [docSearch, setDocSearch] = useState("");
  const [docSortDesc, setDocSortDesc] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Adicionar ficheiro a entrada de documento existente
  const [addFileDoc, setAddFileDoc] = useState(null);
  const [addFileFile, setAddFileFile] = useState(null);
  const [addFileUploading, setAddFileUploading] = useState(false);
  const [addFileError, setAddFileError] = useState("");

  const [honSortDesc, setHonSortDesc] = useState(false);
  const [honAtribuirA, setHonAtribuirA] = useState("");
  const [honFiltroSaldado, setHonFiltroSaldado] = useState(false);
  const [viewReu, setViewReu] = useState(null);

  const filteredDocs = useMemo(() =>
    documentos
      .filter(d => !docSearch || (d.designacao||"").toLowerCase().includes(docSearch.toLowerCase()) || (d.file_name||"").toLowerCase().includes(docSearch.toLowerCase()))
      .sort((a,b) => docSortDesc ? new Date(b.created_at)-new Date(a.created_at) : new Date(a.created_at)-new Date(b.created_at)),
    [documentos, docSearch, docSortDesc]
  );

  const totalHonorarios = useMemo(() =>
    honorarios.reduce((s, h) => s + (parseFloat(String(h.debito_credito || 0).replace(",", ".")) || 0), 0),
    [honorarios]
  );
  const fmtHonTotal = totalHonorarios.toFixed(2).replace(".", ",") + " €";

  const sortedHonorarios = useMemo(() => {
    const sorted = [...honorarios].sort((a, b) => {
      const da = a.data || ""; const db_ = b.data || "";
      if (!da && !db_) return 0;
      if (!da) return 1; if (!db_) return -1;
      return honSortDesc ? db_.localeCompare(da) : da.localeCompare(db_);
    });
    return honFiltroSaldado ? sorted.filter(h => (h.saldado || "Não") !== "Sim") : sorted;
  }, // eslint-disable-line react-hooks/exhaustive-deps
  [honorarios, honSortDesc, honFiltroSaldado]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/processos/${id}`, { headers: getAuthHeader() });
    setProcesso(data); setForm(data); setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/utilizadores`, { headers: getAuthHeader() }).then(r => setUsers(r.data)).catch(()=>{}); }, []); // eslint-disable-next-line react-hooks/exhaustive-deps
  // ↑ API e getAuthHeader são referências de módulo estáveis — omissão intencional das deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadSub = useCallback(async (tab) => {
    setSubLoading(true);
    const h = getAuthHeader();
    try {
      if (tab==="documentos") { const r = await axios.get(`${API}/processos/${id}/documentos`,{headers:h}); setDocumentos(r.data); }
      if (tab==="honorarios") { const r = await axios.get(`${API}/processos/${id}/honorarios`,{headers:h}); setHonorarios(r.data); }
      if (tab==="reunioes") { const r = await axios.get(`${API}/processos/${id}/reunioes`,{headers:h}); setReunioes(r.data); }
      if (tab==="despesas") { const r = await axios.get(`${API}/processos/${id}/despesas`,{headers:h}); setDespesas(r.data); }
      if (tab==="prazos") { const r = await axios.get(`${API}/processos/${id}/prazos`,{headers:h}); setPrazos(r.data); }
    } finally { setSubLoading(false); }
  }, [id]);

  useEffect(() => {
    if (["documentos","honorarios","reunioes","despesas","prazos"].includes(activeTab)) loadSub(activeTab);
  }, [activeTab, loadSub]);

  const handleSave = async () => {
    setSaving(true);
    try { await axios.put(`${API}/processos/${id}`, form, { headers: getAuthHeader() }); setEditMode(false); load(); }
    catch (e) { alert(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Eliminar este processo e todos os dados associados?")) return;
    await axios.delete(`${API}/processos/${id}`, { headers: getAuthHeader() });
    navigate("/processos");
  };

  const handleAddDoc = async (e) => {
    e.preventDefault(); setUploading(true); setDocUploadError("");
    try {
      if (docFiles.length === 0) {
        const fd = new FormData();
        fd.append("designacao", subForm.designacao || "");
        fd.append("file_url", ""); fd.append("file_name", ""); fd.append("file_size", 0);
        await axios.post(`${API}/processos/${id}/documentos`, fd, { headers: getAuthHeader() });
        setShowDocModal(false); setSubForm({}); setDocFiles([]);
      } else {
        // Upload individual por ficheiro — falhas num não bloqueiam os outros
        const failed = [];
        for (const { file } of docFiles) {
          try {
            const uploaded = await chunkedUpload(file);
            const fd = new FormData();
            fd.append("designacao", subForm.designacao || file.name);
            fd.append("file_url", uploaded.file_url || "");
            fd.append("file_name", uploaded.file_name || "");
            fd.append("file_size", uploaded.file_size || 0);
            await axios.post(`${API}/processos/${id}/documentos`, fd, { headers: getAuthHeader() });
          } catch (fileErr) {
            failed.push(file.name);
          }
        }
        if (failed.length === 0) {
          setShowDocModal(false); setSubForm({}); setDocFiles([]); setDocUploadError("");
        } else {
          // Mostrar quais falharam — fechar modal se pelo menos 1 foi guardado
          const saved = docFiles.length - failed.length;
          setDocUploadError(
            `${saved > 0 ? `${saved} ficheiro(s) guardado(s). ` : ""}Falhou: ${failed.join(", ")}. Tente novamente.`
          );
          if (saved > 0) {
            // Remover apenas os que falharam da lista
            setDocFiles(prev => prev.filter(({ file }) => failed.includes(file.name)));
          }
        }
      }
    } catch(e){
      setDocUploadError(e.response?.data?.detail || e.message || "Erro ao guardar. Tente novamente.");
    } finally {
      setUploading(false);
      loadSub("documentos"); // recarrega sempre, mesmo em caso de erro parcial
    }
  };

  const handleAddSub = (endpoint, onClose, reloadFn) => async (e) => {
    e.preventDefault(); setUploading(true);
    try {
      await axios.post(`${API}/processos/${id}/${endpoint}`, { ...subForm, processo_id: id }, { headers: getAuthHeader() });
      onClose(); setSubForm({}); reloadFn();
    } catch(e){ alert(e.response?.data?.detail||"Erro"); }
    finally { setUploading(false); }
  };

  const handleDelSub = (endpoint, reloadFn) => async (subId) => {
    if (!window.confirm("Eliminar?")) return;
    await axios.delete(`${API}/processos/${id}/${endpoint}/${subId}`, { headers: getAuthHeader() });
    reloadFn();
  };

  const getAllDocFiles = (d) => {
    if (!d) return [];
    const base = process.env.REACT_APP_BACKEND_URL;
    const results = [];
    if (d.file_url)
      results.push({ url: `${base}${d.file_url}`, name: d.file_name || "Ficheiro" });
    (d.files_extra || []).forEach(fe => {
      if (fe.file_url)
        results.push({ url: `${base}${fe.file_url}`, name: fe.file_name || "Ficheiro" });
    });
    if (d.original_filename)
      d.original_filename.split(",").filter(Boolean).forEach(fn => {
        const clean = fn.trim();
        results.push({ url: `${base}/api/ftp-file?fname=${encodeURIComponent(clean)}`, name: clean.replace(/^\d+_/, "") });
      });
    return results;
  };

  const handleEditHon = async (e) => {
    e.preventDefault(); setUploading(true);
    try {
      await axios.put(`${API}/processos/${id}/honorarios/${editHon.id}`, subForm, { headers: getAuthHeader() });
      setShowHonModal(false); setEditHon(null); setSubForm({}); loadSub("honorarios");
    } catch(err) { alert(err.response?.data?.detail || "Erro"); }
    finally { setUploading(false); }
  };

  const handleAddHonorario = async (e) => {
    e.preventDefault(); setUploading(true);
    try {
      const payload = { ...subForm, processo_id: id };
      if ((user?.role === "administracao" || user?.role === "admin") && honAtribuirA) payload.responsavel_nome = honAtribuirA;
      await axios.post(`${API}/processos/${id}/honorarios`, payload, { headers: getAuthHeader() });
      setShowHonModal(false); setSubForm({}); setHonAtribuirA(""); loadSub("honorarios");
    } catch(err) { alert(err.response?.data?.detail || "Erro"); }
    finally { setUploading(false); }
  };

  const handleEditDes = async (e) => {
    e.preventDefault(); setUploading(true);
    try {
      await axios.put(`${API}/processos/${id}/despesas/${editDes.id}`, subForm, { headers: getAuthHeader() });
      setShowDesModal(false); setEditDes(null); setSubForm({}); loadSub("despesas");
    } catch(err) { alert(err.response?.data?.detail || "Erro"); }
    finally { setUploading(false); }
  };

  const getUserName = (uid) => { const u = users.find(x=>x.id===uid); return u ? `${u.nome} ${u.apelido}` : uid; };

  const handleAddFileToDoc = async () => {
    if (!addFileFile || !addFileDoc) return;
    setAddFileUploading(true); setAddFileError("");
    try {
      const uploaded = await chunkedUpload(addFileFile);
      const fd = new FormData();
      fd.append("file_url", uploaded.file_url);
      fd.append("file_name", uploaded.file_name);
      fd.append("file_size", uploaded.file_size || 0);
      await axios.post(`${API}/processos/${id}/documentos/${addFileDoc.id}/ficheiro`, fd, { headers: getAuthHeader() });
      setAddFileDoc(null); setAddFileFile(null);
      loadSub("documentos");
    } catch (e) {
      setAddFileError(e.response?.data?.detail || "Erro ao adicionar ficheiro. Tente novamente.");
    } finally {
      setAddFileUploading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-7 h-7 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin"/></div>;
  if (!processo) return <div className="text-center text-slate-500 py-20">Processo não encontrado</div>;

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button onClick={()=>navigate("/processos")} className="mt-1 text-slate-400 hover:text-slate-600 transition-colors"><ArrowLeft size={20}/></button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900">Processo {processo.numero_processo}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SITUACAO_COLORS[processo.situacao]||"bg-slate-100 text-slate-700"}`}>{processo.situacao}</span>
              {processo.prioridade==="Urgente" && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><AlertTriangle size={10}/> Urgente</span>}
            </div>
            <p className="text-slate-500 text-sm mt-0.5">{processo.nome_cliente || "Sem cliente"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <button onClick={()=>{setEditMode(false);setForm(processo);}} className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50"><X size={14}/> Cancelar</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-processo-edit-btn"
                className="flex items-center gap-1.5 px-3 py-2 bg-[#0b679e] hover:bg-[#084e78] text-white rounded-lg text-sm font-medium disabled:opacity-60">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <Save size={14}/>} Guardar
              </button>
            </>
          ) : (
            <>
              <button onClick={()=>setEditMode(true)} data-testid="edit-processo-btn"
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50"><Edit2 size={14}/> Editar</button>
              {user?.role==="admin" && <button onClick={handleDelete} data-testid="delete-processo-btn"
                className="flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50"><Trash2 size={14}/> Eliminar</button>}
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {[["detalhes","Detalhes"],["cliente","Cliente"],["contraparte","Contraparte"],["documentos","Documentos"],["honorarios","Honorários"],["reunioes","Reuniões"],["despesas","Despesas"],["prazos","Prazos"]].map(([k,l])=>(
            <Tab key={k} label={l} active={activeTab===k} onClick={()=>setActiveTab(k)} testId={`tab-${k}`}/>
          ))}
        </div>

        <div className="p-6">
          {activeTab==="detalhes" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <Field label="Nº Processo" value={processo.numero_processo} edit={editMode} name="numero_processo" form={form} setForm={setForm}/>
              <Field label="Pasta" value={processo.pasta} edit={editMode} name="pasta" form={form} setForm={setForm}/>
              <Field label="Prioridade" value={processo.prioridade} edit={editMode} name="prioridade" form={form} setForm={setForm} options={["Normal","Urgente"]}/>
              <Field label="Situação" value={processo.situacao} edit={editMode} name="situacao" form={form} setForm={setForm} options={SITUACOES}/>
              <Field label="Entidade" value={processo.entidade} edit={editMode} name="entidade" form={form} setForm={setForm} options={ENTIDADES}/>
              <Field label="Tipo de Processo" value={processo.tipo_processo} edit={editMode} name="tipo_processo" form={form} setForm={setForm}/>
              <Field label="Data de Entrada" value={processo.data_entrada} edit={editMode} name="data_entrada" form={form} setForm={setForm} type="date"/>
              <Field label="Nº na Entidade" value={processo.numero_entidade} edit={editMode} name="numero_entidade" form={form} setForm={setForm}/>
              <Field label="Outra Entidade" value={processo.outra_entidade} edit={editMode} name="outra_entidade" form={form} setForm={setForm}/>
              <div className="md:col-span-2 lg:col-span-3">
                <Field label="Assunto" value={processo.assunto} edit={editMode} name="assunto" form={form} setForm={setForm} type="textarea"/>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Field label="Observações" value={processo.observacoes} edit={editMode} name="observacoes" form={form} setForm={setForm} type="textarea"/>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Equipa</p>
                {editMode ? (
                  <div className="border border-slate-300 rounded-lg p-3 grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                    {users.map(u=>(
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={(form.equipa||[]).includes(u.id)}
                          onChange={e=>setForm({...form,equipa:e.target.checked?[...(form.equipa||[]),u.id]:(form.equipa||[]).filter(x=>x!==u.id)})}
                          className="rounded"/>
                        {u.nome} {u.apelido}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(processo.equipa||[]).length===0 ? <span className="text-slate-400 text-sm">—</span> :
                    (processo.equipa||[]).map(uid=><span key={uid} className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-full">{getUserName(uid)}</span>)}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab==="cliente" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Nome do Cliente" value={processo.nome_cliente} edit={editMode} name="nome_cliente" form={form} setForm={setForm}/>
              <Field label="Contribuinte (NIF/NIPC)" value={processo.contribuinte_cliente} edit={editMode} name="contribuinte_cliente" form={form} setForm={setForm}/>
              <div className="md:col-span-2"><Field label="Morada" value={processo.morada_cliente} edit={editMode} name="morada_cliente" form={form} setForm={setForm}/></div>
              <Field label="Código Postal" value={processo.codigo_postal_cliente} edit={editMode} name="codigo_postal_cliente" form={form} setForm={setForm}/>
              <Field label="Localidade" value={processo.localidade_cliente} edit={editMode} name="localidade_cliente" form={form} setForm={setForm}/>
              <Field label="Contato" value={processo.contato_cliente} edit={editMode} name="contato_cliente" form={form} setForm={setForm}/>
              <Field label="Email" value={processo.email_cliente} edit={editMode} name="email_cliente" form={form} setForm={setForm} type="email"/>
            </div>
          )}

          {activeTab==="contraparte" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2"><Field label="Nome da Contraparte" value={processo.nome_contraparte} edit={editMode} name="nome_contraparte" form={form} setForm={setForm}/></div>
              <Field label="Contato Contraparte" value={processo.contato_contraparte} edit={editMode} name="contato_contraparte" form={form} setForm={setForm}/>
              <Field label="Email Contraparte" value={processo.email_contraparte} edit={editMode} name="email_contraparte" form={form} setForm={setForm} type="email"/>
              <div className="md:col-span-2"><Field label="Mandatário da Contraparte" value={processo.mandatario_contraparte} edit={editMode} name="mandatario_contraparte" form={form} setForm={setForm}/></div>
              <Field label="Contato Mandatário" value={processo.contato_mandatario} edit={editMode} name="contato_mandatario" form={form} setForm={setForm}/>
              <Field label="Email Mandatário" value={processo.email_mandatario} edit={editMode} name="email_mandatario" form={form} setForm={setForm} type="email"/>
            </div>
          )}

          {activeTab==="documentos" && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={docSearch} onChange={e => setDocSearch(e.target.value)}
                    placeholder="Pesquisar documentos..." data-testid="doc-search-input"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
                </div>
                <button onClick={() => setDocSortDesc(v => !v)} data-testid="doc-sort-btn"
                  className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 whitespace-nowrap">
                  <ArrowUpDown size={13}/> {docSortDesc ? "Mais recente" : "Mais antigo"}
                </button>
              </div>
            <SubEntityTable
              headers={HEADERS_DOCS}
              rows={filteredDocs}
              loading={subLoading}
              onAdd={()=>{setSubForm({});setDocFiles([]);setDocUploadError("");setShowDocModal(true);}}
              onDelete={handleDelSub("documentos",()=>loadSub("documentos"))}
              renderRow={(d,i,onDel)=>{
                const allFiles = [];
                if (d.file_url) allFiles.push({ url: d.file_url, name: d.file_name || "Ficheiro" });
                (d.files_extra || []).forEach(f => { if (f.file_url) allFiles.push({ url: f.file_url, name: f.file_name || "Ficheiro" }); });

                const renderDocFile = () => {
                  if (allFiles.length > 0) return (
                    <div className="flex flex-col gap-1">
                      {allFiles.map((f, fi) => (
                        <a key={f.url || `file-${fi}`} href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#0b679e] hover:text-[#F5A623] text-xs transition-colors truncate max-w-xs" title={f.name}>
                          <Paperclip size={11}/> {f.name}
                        </a>
                      ))}
                    </div>
                  );
                  if (d.original_filename) return (
                    <div className="flex flex-col gap-1">
                      {parseFtpLinks(d.original_filename).map(({ key, url, displayName }) => (
                        <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#0b679e] hover:text-[#F5A623] text-xs transition-colors truncate max-w-xs"
                          title={key}>
                          <Paperclip size={11}/> {displayName}
                        </a>
                      ))}
                    </div>
                  );
                  return <span className="text-slate-400 text-xs">Sem ficheiro</span>;
                };
                return (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 text-slate-900">
                    <div className="flex items-center gap-1.5">
                      {d.designacao}
                      {(d.files_extra?.length > 0) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#0b679e]/10 text-[#0b679e]">
                          {allFiles.length}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">{renderDocFile()}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{d.created_at?.slice(0,10)||"—"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid="btn-add-ficheiro"
                        onClick={() => { setAddFileDoc(d); setAddFileFile(null); setAddFileError(""); }}
                        title="Adicionar ficheiro a esta entrada"
                        className="text-emerald-500 hover:text-emerald-700 transition-colors">
                        <PlusCircle size={14}/>
                      </button>
                      {getAllDocFiles(d).length > 0 && (
                        <button data-testid="btn-preview-doc" title="Ver ficheiros"
                          onClick={() => { setPreviewDoc(d); }}
                          className="text-[#0b679e] hover:text-[#084e78] transition-colors">
                          <Eye size={14}/>
                        </button>
                      )}
                      <button onClick={()=>onDel(d.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              );}}
            />
            </>
          )}

          {activeTab==="honorarios" && (
            <>
              <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
                <div className="flex items-center gap-3" data-testid="honorarios-total-header">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total de Honorários Devidos</span>
                  <span className="text-xl font-bold text-[#0b679e]" data-testid="honorarios-total-value">{fmtHonTotal}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setHonSortDesc(v => !v)} data-testid="hon-sort-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-600 hover:bg-slate-50 whitespace-nowrap">
                    <ArrowUpDown size={13}/> {honSortDesc ? "Mais recente" : "Mais antigo"}
                  </button>
                  <button onClick={() => setHonFiltroSaldado(p => !p)} data-testid="btn-filtro-saldado"
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap ${honFiltroSaldado ? "bg-red-50 border-red-300 text-red-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                    <Filter size={13}/> {honFiltroSaldado ? "Só em dívida" : "Todos"}
                  </button>
                  <button onClick={() => setShowNota(true)} data-testid="btn-nota-honorarios"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#0b679e] text-[#0b679e] hover:bg-blue-50 transition-colors">
                    <FileText size={13} /> Nota de Honorários
                  </button>
                </div>
              </div>
            <SubEntityTable
              headers={HEADERS_HON}
              rows={sortedHonorarios} loading={subLoading}
              onAdd={()=>{setSubForm({designacao:"",data:"",tipo_pagamento:""});setEditHon(null);setHonAtribuirA("");setShowHonModal(true);}}
              onDelete={handleDelSub("honorarios",()=>loadSub("honorarios"))}
              renderRow={(h,i,onDel)=>(
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5">{h.designacao||"—"}</td>
                  <td className="px-3 py-2.5 font-medium">{Number(h.debito_credito||0).toFixed(2)} €</td>
                  <td className="px-3 py-2.5 text-slate-500">{h.data||"—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{h.tipo_pagamento||"—"}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${(h.saldado||"Não")==="Sim"?"bg-green-100 text-green-700":"bg-red-100 text-red-700"}`}>{h.saldado||"Não"}</span></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <button data-testid="btn-edit-hon" onClick={()=>{ setEditHon(h); setSubForm({designacao:h.designacao||"",debito_credito:h.debito_credito||0,data:h.data||"",tipo_pagamento:h.tipo_pagamento||"",saldado:h.saldado||"Não"}); setShowHonModal(true); }} className="text-blue-400 hover:text-blue-600"><Edit2 size={14}/></button>
                      <button onClick={()=>onDel(h.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              )}
              totalFooter={honorarios.length > 0 ? (
                <tr className="bg-[#eaf4fb] border-t-2 border-[#0b679e]">
                  <td colSpan={4} className="px-3 py-2.5 text-sm font-bold text-[#0b679e]">Total de Honorários Devidos</td>
                  <td className="px-3 py-2.5 text-sm font-bold text-[#0b679e] text-right" data-testid="honorarios-total-footer">{fmtHonTotal}</td>
                </tr>
              ) : null}
            />
            </>
          )}

          {activeTab==="reunioes" && (
            <SubEntityTable
              headers={HEADERS_REU}
              rows={reunioes} loading={subLoading}
              onAdd={()=>{setSubForm({data_inicio:"",resumo:"",descricao:"",intervenientes:[]});setShowReuModal(true);}}
              onDelete={handleDelSub("reunioes",()=>loadSub("reunioes"))}
              renderRow={(r,i,onDel)=>(
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 text-slate-900 whitespace-nowrap">{r.data_inicio||"—"}</td>
                  <td className="px-3 py-2.5 text-slate-700 max-w-xs truncate">{r.resumo||"—"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{(r.intervenientes||[]).map(uid=>getUserName(uid)).join(", ")||"—"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {(r.descricao || r.resumo) && (
                        <button onClick={()=>setViewReu(r)} data-testid="btn-view-reuniao"
                          title="Ver apontamentos" className="text-[#0b679e] hover:text-[#084e78] transition-colors">
                          <Eye size={14}/>
                        </button>
                      )}
                      <button onClick={()=>onDel(r.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              )}
            />
          )}

          {activeTab==="despesas" && (
            <SubEntityTable
              headers={HEADERS_DES}
              rows={despesas} loading={subLoading}
              onAdd={()=>{setSubForm({designacao:"",data:"",tipo_pagamento:"",saldado:"Não"});setEditDes(null);setShowDesModal(true);}}
              onDelete={handleDelSub("despesas",()=>loadSub("despesas"))}
              renderRow={(d,i,onDel)=>(
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5">{d.designacao||"—"}</td>
                  <td className="px-3 py-2.5 font-medium">{Number(d.debito_credito||0).toFixed(2)} €</td>
                  <td className="px-3 py-2.5 text-slate-500">{d.data||"—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{d.tipo_pagamento||"—"}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${d.saldado==="Sim"?"bg-green-100 text-green-700":"bg-red-100 text-red-700"}`}>{d.saldado||"Não"}</span></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <button data-testid="btn-edit-des" onClick={()=>{ setEditDes(d); setSubForm({designacao:d.designacao||"",debito_credito:d.debito_credito||0,data:d.data||"",tipo_pagamento:d.tipo_pagamento||"",saldado:d.saldado||"Não"}); setShowDesModal(true); }} className="text-blue-400 hover:text-blue-600"><Edit2 size={14}/></button>
                      <button onClick={()=>onDel(d.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              )}
            />
          )}

          {activeTab==="prazos" && (
            <SubEntityTable
              headers={HEADERS_PRA}
              rows={prazos} loading={subLoading}
              onAdd={()=>{setSubForm({data:"",respondido:"Não",descricao:""});setShowPraModal(true);}}
              onDelete={handleDelSub("prazos",()=>loadSub("prazos"))}
              renderRow={(p,i,onDel)=>(
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-slate-900">{p.data||"—"}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${p.respondido==="Sim"?"bg-green-100 text-green-700":"bg-red-100 text-red-700"}`}>{p.respondido||"Não"}</span></td>
                  <td className="px-3 py-2.5 text-slate-500 max-w-xs truncate">{p.descricao||"—"}</td>
                  <td className="px-3 py-2.5"><button onClick={()=>onDel(p.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button></td>
                </tr>
              )}
            />
          )}
        </div>
      </div>

      {/* Sub-entity Modals */}
      {showDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Novo Documento</h3>
              <button onClick={()=>{setShowDocModal(false);setDocUploadError("");setDocFiles([]);}}><X size={18}/></button>
            </div>
            <form onSubmit={handleAddDoc} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Designação {docFiles.length > 1 ? "(usada em todos os ficheiros)" : "*"}</label>
                <input value={subForm.designacao||""} onChange={e=>setSubForm({...subForm,designacao:e.target.value})}
                  required={docFiles.length === 0}
                  placeholder={docFiles.length > 1 ? "Opcional — usa nome do ficheiro se vazio" : ""}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ficheiros</label>
                {/* Lista acumulada */}
                {docFiles.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {docFiles.map(({ file, uid }) => (
                      <div key={uid} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                        <span className="text-xs text-slate-700 truncate max-w-[300px]">{file.name}</span>
                        <button type="button" onClick={()=>setDocFiles(prev=>prev.filter(f=>f.uid!==uid))}
                          className="ml-2 text-slate-400 hover:text-red-500 flex-shrink-0"><X size={13}/></button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Botão para adicionar mais ficheiros */}
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <Plus size={14} className="text-[#0b679e]"/>
                  <span className="text-sm text-slate-500">{docFiles.length === 0 ? "Escolher ficheiro(s)..." : "Adicionar mais ficheiros..."}</span>
                  <input type="file" data-testid="doc-file-input" multiple
                    onChange={e => {
                      const newFiles = Array.from(e.target.files).map((f, i) => ({ file: f, uid: `${Date.now()}-${i}-${f.name}` }));
                      setDocFiles(prev => [...prev, ...newFiles]); setDocUploadError(""); e.target.value="";
                    }}
                    accept=".pdf,.doc,.docx,.xlsx,.jpg,.png,.txt"
                    className="hidden" />
                </label>
                {docFiles.length > 1 && (
                  <p className="text-xs text-[#0b679e] mt-1">Serão criadas <strong>{docFiles.length}</strong> entradas, uma por ficheiro.</p>
                )}
              </div>
              {docUploadError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle size={14} className="flex-shrink-0" /> {docUploadError}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={()=>{setShowDocModal(false);setDocUploadError("");setDocFiles([]);}} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
                <button type="submit" disabled={uploading} className="px-4 py-2 bg-[#0b679e] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center gap-2">
                  {uploading&&<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                  {uploading ? `A carregar${docFiles.length > 1 ? ` (${docFiles.length} ficheiros)` : ""}...` : "Guardar"}                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Adicionar ficheiro a documento existente */}
      {addFileDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold flex items-center gap-2"><PlusCircle size={16} className="text-emerald-600"/> Adicionar Ficheiro</h3>
              <button onClick={() => { setAddFileDoc(null); setAddFileFile(null); setAddFileError(""); }}><X size={18}/></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              A acrescentar à entrada: <strong className="text-slate-700">{addFileDoc.designacao}</strong>
              {addFileDoc.files_extra?.length > 0 && (
                <span className="ml-2 text-xs text-[#0b679e]">({1 + addFileDoc.files_extra.length} ficheiro{1 + addFileDoc.files_extra.length > 1 ? "s" : ""} existentes)</span>
              )}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ficheiro *</label>
                <input type="file" data-testid="input-add-ficheiro"
                  onChange={e => setAddFileFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[#0b679e]/10 file:text-[#0b679e] hover:file:bg-[#0b679e]/20 cursor-pointer"/>
                {addFileFile && (
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                    <Paperclip size={11}/> {addFileFile.name} ({(addFileFile.size / 1024).toFixed(0)} KB)
                  </p>
                )}
              </div>
              {addFileError && <p className="text-xs text-red-500 font-medium">{addFileError}</p>}
              <div className="flex justify-end gap-3 pt-1">
                <button type="button"
                  onClick={() => { setAddFileDoc(null); setAddFileFile(null); setAddFileError(""); }}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
                <button type="button" data-testid="btn-confirmar-add-ficheiro"
                  onClick={handleAddFileToDoc}
                  disabled={!addFileFile || addFileUploading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center gap-2 hover:bg-emerald-700 transition-colors">
                  {addFileUploading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                  {addFileUploading ? "A enviar..." : "Adicionar Ficheiro"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{editHon ? "Editar Honorário" : "Novo Honorário"}</h3>
              <button onClick={()=>{ setShowHonModal(false); setEditHon(null); setHonAtribuirA(""); }}><X size={18}/></button>
            </div>
            <form onSubmit={editHon ? handleEditHon : handleAddHonorario} className="space-y-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Designação</label><input value={subForm.designacao||""} onChange={e=>setSubForm({...subForm,designacao:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Débito/Crédito (€)</label><input type="number" step="0.01" placeholder="0,00" value={subForm.debito_credito ?? ""} onChange={e=>setSubForm({...subForm,debito_credito:e.target.value===''?'':parseFloat(e.target.value)||0})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Data</label><input type="date" value={subForm.data||""} onChange={e=>setSubForm({...subForm,data:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Tipo de Pagamento</label><select value={subForm.tipo_pagamento||""} onChange={e=>setSubForm({...subForm,tipo_pagamento:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"><option value="">—</option>{TIPO_PAGAMENTO.map(t=><option key={t}>{t}</option>)}</select></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Saldado?</label><select value={subForm.saldado||"Não"} onChange={e=>setSubForm({...subForm,saldado:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"><option>Não</option><option>Sim</option></select></div>
              {!editHon && (user?.role === "administracao" || user?.role === "admin") && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Atribuir a <span className="text-slate-400 font-normal">(Rendimento)</span>
                  </label>
                  <select
                    data-testid="hon-atribuir-a"
                    value={honAtribuirA}
                    onChange={e => setHonAtribuirA(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                    <option value="">— Sem atribuição (registo próprio) —</option>
                    {users.map(u => (
                      <option key={u.id} value={`${u.nome} ${u.apelido}`.trim()}>
                        {u.nome} {u.apelido}
                      </option>
                    ))}
                  </select>
                  {honAtribuirA && (
                    <p className="text-xs text-[#0b679e] mt-1">
                      Este honorário aparecerá no Rendimento de <strong>{honAtribuirA}</strong>
                    </p>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={()=>{ setShowHonModal(false); setEditHon(null); setHonAtribuirA(""); }} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
                <button type="submit" disabled={uploading} className="px-4 py-2 bg-[#0b679e] text-white rounded-lg text-sm font-medium disabled:opacity-60">{editHon ? "Atualizar" : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReuModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold">Nova Reunião</h3><button onClick={()=>setShowReuModal(false)}><X size={18}/></button></div>
            <form onSubmit={handleAddSub("reunioes",()=>setShowReuModal(false),()=>loadSub("reunioes"))} className="space-y-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Data/Hora Início *</label><input type="datetime-local" required value={subForm.data_inicio||""} onChange={e=>setSubForm({...subForm,data_inicio:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Resumo</label><textarea value={subForm.resumo||""} onChange={e=>setSubForm({...subForm,resumo:e.target.value})} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Descrição</label><textarea value={subForm.descricao||""} onChange={e=>setSubForm({...subForm,descricao:e.target.value})} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Intervenientes</label>
                <div className="border border-slate-300 rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                  {users.map(u=><label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={(subForm.intervenientes||[]).includes(u.id)} onChange={e=>setSubForm({...subForm,intervenientes:e.target.checked?[...(subForm.intervenientes||[]),u.id]:(subForm.intervenientes||[]).filter(x=>x!==u.id)})} className="rounded"/>{u.nome} {u.apelido}</label>)}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={()=>setShowReuModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button><button type="submit" disabled={uploading} className="px-4 py-2 bg-[#0b679e] text-white rounded-lg text-sm font-medium disabled:opacity-60">Guardar</button></div>
            </form>
          </div>
        </div>
      )}

      {showDesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold">{editDes ? "Editar Despesa" : "Nova Despesa"}</h3><button onClick={()=>{ setShowDesModal(false); setEditDes(null); }}><X size={18}/></button></div>
            <form onSubmit={editDes ? handleEditDes : handleAddSub("despesas",()=>{ setShowDesModal(false); setEditDes(null); },()=>loadSub("despesas"))} className="space-y-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Designação</label><input value={subForm.designacao||""} onChange={e=>setSubForm({...subForm,designacao:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">€ Valor</label><input type="number" step="0.01" placeholder="0,00" value={subForm.debito_credito ?? ""} onChange={e=>setSubForm({...subForm,debito_credito:e.target.value===''?'':parseFloat(e.target.value)||0})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Data</label><input type="date" value={subForm.data||""} onChange={e=>setSubForm({...subForm,data:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Tipo Pagamento</label><select value={subForm.tipo_pagamento||""} onChange={e=>setSubForm({...subForm,tipo_pagamento:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"><option value="">—</option>{TIPO_PAGAMENTO.map(t=><option key={t}>{t}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Saldado?</label><select value={subForm.saldado||"Não"} onChange={e=>setSubForm({...subForm,saldado:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"><option>Não</option><option>Sim</option></select></div>
              </div>
              <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={()=>{ setShowDesModal(false); setEditDes(null); }} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button><button type="submit" disabled={uploading} className="px-4 py-2 bg-[#0b679e] text-white rounded-lg text-sm font-medium disabled:opacity-60">{editDes ? "Atualizar" : "Guardar"}</button></div>
            </form>
          </div>
        </div>
      )}

      {showPraModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold">Novo Prazo</h3><button onClick={()=>setShowPraModal(false)}><X size={18}/></button></div>
            <form onSubmit={handleAddSub("prazos",()=>setShowPraModal(false),()=>loadSub("prazos"))} className="space-y-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Data *</label><input type="date" required value={subForm.data||""} onChange={e=>setSubForm({...subForm,data:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Respondido?</label><select value={subForm.respondido||"Não"} onChange={e=>setSubForm({...subForm,respondido:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"><option>Não</option><option>Sim</option></select></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Descrição</label><textarea value={subForm.descricao||""} onChange={e=>setSubForm({...subForm,descricao:e.target.value})} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={()=>setShowPraModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button><button type="submit" disabled={uploading} className="px-4 py-2 bg-[#0b679e] text-white rounded-lg text-sm font-medium disabled:opacity-60">Guardar</button></div>
            </form>
          </div>
        </div>
      )}
      {viewReu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={()=>setViewReu(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50 rounded-t-2xl">
              <div>
                <p className="text-sm font-bold text-slate-800">{viewReu.resumo || "Apontamentos de Reunião"}</p>
                <p className="text-xs text-slate-500 mt-0.5">{viewReu.data_inicio || "—"}</p>
              </div>
              <button onClick={()=>setViewReu(null)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400"><X size={16}/></button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {viewReu.descricao ? (
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{viewReu.descricao}</p>
              ) : (
                <p className="text-sm text-slate-400 italic">Sem apontamentos registados.</p>
              )}
            </div>
            {(viewReu.intervenientes||[]).length > 0 && (
              <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Intervenientes</p>
                <p className="text-sm text-slate-700">{(viewReu.intervenientes||[]).map(uid=>getUserName(uid)).join(", ")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showNota && processo && (
        <NotaHonorariosModal processo={processo} honorarios={honorarios} onClose={() => setShowNota(false)} />
      )}

      {/* Modal de Ficheiros */}
      {previewDoc && (() => {
        const allFiles = getAllDocFiles(previewDoc);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPreviewDoc(null)}>
            <div className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-lg" onClick={e => e.stopPropagation()}>
              {/* Cabeçalho */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50 rounded-t-2xl">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={16} className="text-[#0b679e] shrink-0"/>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{previewDoc.designacao || "Documento"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{allFiles.length} ficheiro{allFiles.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <button data-testid="btn-close-preview" onClick={() => setPreviewDoc(null)} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors text-slate-400">
                  <X size={18}/>
                </button>
              </div>
              {/* Lista de Ficheiros */}
              <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                {allFiles.length > 0 ? allFiles.map((file, idx) => (
                  <a key={file.url || idx}
                     href={file.url}
                     target="_blank"
                     rel="noopener noreferrer"
                     data-testid={`file-open-link-${idx}`}
                     className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-[#0b679e]/5 border border-slate-200 hover:border-[#0b679e]/30 rounded-xl transition-all group">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0 w-8 h-8 bg-[#0b679e]/10 rounded-lg flex items-center justify-center">
                        <FileText size={15} className="text-[#0b679e]"/>
                      </div>
                      <span className="text-sm text-slate-700 truncate group-hover:text-[#0b679e] transition-colors">{file.name}</span>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5 text-xs text-[#0b679e] font-medium whitespace-nowrap">
                      <ExternalLink size={12}/> Abrir
                    </div>
                  </a>
                )) : (
                  <p className="text-sm text-slate-400 italic text-center py-6">Sem ficheiros disponíveis</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
